import React, { useState, useEffect } from 'react';

import {
  UserOutlined,
  LockOutlined,
  SafetyOutlined,
  ArrowLeftOutlined,
  TeamOutlined
} from '@ant-design/icons';
import AppSetupTrigger from '@components/operator/AppSetupTrigger';
import ClearSessionLink from '@components/shared/ClearSessionLink';
import LanguageSwitcher from '@components/shared/LanguageSwitcher';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { getAssetPath } from '@utils/assetPaths';
import {
  Form,
  Input,
  Button,
  Alert,
  Space,
  message
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle, keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Global body styles
const GlobalLoginStyles = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }
  html, body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }
  body {
    background: #f5f5f5;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  }
`;

// Main Container
const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: #f5f5f5;
  position: relative;
`;

// Left Side - Brand & Info
const LeftSide = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #2D5F9F 0%, #4A7BBF 50%, #5B8DC7 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    top: -100px;
    right: -100px;
  }

  &::after {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 50%;
    bottom: -80px;
    left: -80px;
  }

  @media (max-width: 968px) {
    display: none;
  }
`;

const BrandContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 480px;
`;

const BrandLogo = styled.img`
  height: 60px;
  width: auto;
  margin-bottom: 8px;
`;

const VantaBadgeLight = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 5px;
  background: linear-gradient(180deg, #ffffff 0%, #e2e8f0 50%, #cbd5e1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 32px;
`;

const BrandTitle = styled.h1`
  color: white;
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 20px 0;
  letter-spacing: -1px;
  line-height: 1.2;

  @media (max-width: 1200px) {
    font-size: 40px;
  }
`;

const BrandSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.95);
  font-size: 20px;
  margin: 0 0 40px 0;
  line-height: 1.6;

  @media (max-width: 1200px) {
    font-size: 18px;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 40px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 16px;

  &::before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

// Right Side - Login Form
const RightSide = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: #f5f5f5;

  @media (max-width: 968px) {
    flex: none;
    width: 100%;
  }

  @media (max-width: 576px) {
    padding: 16px;
  }
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: white;
  border-radius: 16px;
  padding: 40px 44px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 576px) {
    padding: 32px 24px;
  }
`;

// Language Switcher
const LanguageSwitcherWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 1000;
`;

// Header
const LoginHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const MobileLogo = styled.img`
  display: block;
  height: 56px;
  width: auto;
  margin: 0 auto 8px;

  @media (min-width: 969px) {
    display: none;
  }
`;

const VantaBadge = styled.span`
  display: block;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 5px;
  background: linear-gradient(180deg, #334155 0%, #64748b 50%, #1e293b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 16px;

  @media (min-width: 969px) {
    display: none;
  }
`;

const RoleBadge = styled.div`
  display: inline-block;
  background: #E8F1FA;
  color: #2D5F9F;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 20px;
`;

const LoginTitle = styled.h2`
  color: #1a1a1a;
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  letter-spacing: -0.3px;
`;

const LoginSubtitle = styled.p`
  color: #666;
  font-size: 15px;
  margin: 0;
  line-height: 1.5;
`;

// Form
const LoginForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item:last-child {
    margin-bottom: 0;
  }
`;

// Inputs
const StyledInput = styled(Input)`
  height: 48px !important;
  border-radius: 10px !important;
  border: 1.5px solid #e0e0e0 !important;
  font-size: 15px !important;
  transition: all 0.2s ease !important;
  padding: 0 16px !important;

  &:hover {
    border-color: #4A90E2 !important;
  }

  &:focus,
  &.ant-input-focused {
    border-color: #4A90E2 !important;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15) !important;
  }

  .ant-input-prefix {
    margin-right: 10px;
    color: #999;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  &.ant-input-affix-wrapper {
    height: 48px !important;
    border-radius: 10px !important;
    border: 1.5px solid #e0e0e0 !important;
    transition: all 0.2s ease !important;
    padding: 0 16px !important;

    &:hover {
      border-color: #4A90E2 !important;
    }

    &.ant-input-affix-wrapper-focused {
      border-color: #4A90E2 !important;
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15) !important;
    }

    .ant-input {
      font-size: 15px !important;
    }

    .ant-input-prefix {
      margin-right: 10px;
      color: #999;
    }

    .ant-input-suffix .anticon {
      color: #999;
    }
  }
`;

// Button
const LoginButton = styled(Button)`
  height: 48px !important;
  background: linear-gradient(135deg, #2D5F9F, #4A7BBF) !important;
  border: none !important;
  border-radius: 10px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  margin-top: 8px !important;
  transition: all 0.2s ease !important;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #4A7BBF, #5B8DC7) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(45, 95, 159, 0.3) !important;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

// Alert
const StyledAlert = styled(Alert)`
  border-radius: 8px !important;
  margin-bottom: 20px !important;
`;

// Footer

interface CredentialsFormValues {
  workspace: string;
  username: string;
  password: string;
}

interface TotpFormValues {
  code: string;
}

type LoginStep = 'credentials' | 'totp';

const OperatorLogin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, verifyTotp, error: authError, clearError } = useOperatorAuth();
  const { refreshOperatorData } = useOperatorData();
  const [loading, setLoading] = useState(false);
  const [presetWorkspace] = useState<string | null>(() => {
    if (typeof document === 'undefined') return null;
    const node = document.getElementById('app-setup-config');
    if (!node?.textContent) return null;
    try {
      const parsed = JSON.parse(node.textContent);
      const ws = typeof parsed.workspace === 'string' ? parsed.workspace.trim() : '';
      return ws || null;
    } catch (_) {
      return null;
    }
  });
  const [initialWorkspace] = useState<string>(() => {
    if (presetWorkspace) return presetWorkspace;
    if (typeof window === 'undefined') return '';
    const fromLocal = window.localStorage.getItem('last_workspace');
    if (fromLocal) return fromLocal;
    // Fallback: 註冊後 auto_login 端會寫 last_workspace cookie 作為 UX hint
    // （cross-origin localStorage 拿不到，所以靠 cookie 過橋）。讀到後同步寫回
    // localStorage，後續就走原本的 path。
    const match = document.cookie.match(/(?:^|;\s*)last_workspace=([^;]+)/);
    if (match) {
      const value = decodeURIComponent(match[1]);
      try { window.localStorage.setItem('last_workspace', value); } catch (_) {}
      return value;
    }
    return '';
  });
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [totpForm] = Form.useForm();

  // TOTP 狀態
  const [loginStep, setLoginStep] = useState<LoginStep>('credentials'); // 'credentials' | 'totp'

  // 錯誤訊息翻譯，優先當前 step 的 namespace，找不到再 fallback 到另一個 step、最後才顯示 raw code。
  // 防止 TOTP 階段殘留的 error code（如 invalid_totp_code）在 credentials step 顯示成原始字串。
  const tAuthError = (code: string, primaryNs: 'login' | 'totp') => {
    const fallbackNs = primaryNs === 'login' ? 'totp' : 'login';
    return t(`operator.${primaryNs}.${code}`, {
      defaultValue: t(`operator.${fallbackNs}.${code}`, { defaultValue: code })
    });
  };
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [useBackupCode, setUseBackupCode] = useState(false);

  // 檢查 URL 參數，顯示 session expired 訊息
  useEffect(() => {
    const reason = searchParams.get('reason');
    if (reason === 'session_expired') {
      setSessionExpiredMessage(t('operator.login.session_expired'));
      // 清除 URL 參數，避免重新整理時再次顯示
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  // 自動清除 session expired 訊息
  useEffect(() => {
    if (sessionExpiredMessage) {
      const timer = setTimeout(() => {
        setSessionExpiredMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [sessionExpiredMessage]);

  useEffect(() => {
    if (authError) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [authError, clearError]);

  const onFinish = async (values: unknown) => {
    const { workspace: formWorkspace, username, password } = values as CredentialsFormValues;
    const workspace = presetWorkspace || formWorkspace;
    setLoading(true);

    try {
      const result = await login(workspace, username, password);

      if (result.totpRequired) {
        // 需要 TOTP 驗證 → 切換到 TOTP 步驟
        setPendingToken(result.pendingToken ?? null);
        setLoginStep('totp');
        clearError();
        return;
      }

      if (result.success) {
        try { window.localStorage.setItem('last_workspace', workspace); } catch (_) {}
        await refreshOperatorData();
        if (result.totpSetupRequired) {
          navigate('/totp-setup', { replace: true });
        } else {
          navigate('/products', { replace: true });
        }
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetTotpInputForRetry = () => {
    totpForm.resetFields(['code']);
    totpForm.getFieldInstance('code')?.focus?.();
  };

  const onTotpFinish = async (values: unknown) => {
    const { code } = values as TotpFormValues;
    setLoading(true);

    try {
      const result = await verifyTotp(pendingToken!, code);

      if (result.success) {
        await refreshOperatorData();
        if (result.totpSetupRequired) {
          navigate('/totp-setup', { replace: true });
        } else {
          navigate('/products', { replace: true });
        }
      } else if (result.message === 'invalid_pending_token') {
        // pending_token 失效時退回 credentials step；handleBackToCredentials 會 clearError，
        // 因此用 message.warning 顯式提示原因，避免使用者誤以為是系統 bug。
        message.warning(t('operator.totp.invalid_pending_token'));
        handleBackToCredentials();
      } else {
        // 其他失敗（驗證碼錯誤 / 限流 / 未知錯誤）：清空輸入並 refocus，方便使用者直接重打。
        resetTotpInputForRetry();
      }
    } catch (err) {
      console.error('TOTP verify error:', err);
      resetTotpInputForRetry();
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setLoginStep('credentials');
    setPendingToken(null);
    setUseBackupCode(false);
    totpForm.resetFields();
    clearError();
  };

  return (
    <>
      <GlobalLoginStyles />
      <LoginContainer>
        <LeftSide>
          <BrandContent>
            <BrandLogo
              src={getAssetPath('gowarehouse_logo')}
              alt="GoWarehouse"
            />
            <VantaBadgeLight>VANTA EDITION</VantaBadgeLight>
            <BrandTitle>
              {t('operator.login.brand_title')}
            </BrandTitle>
            <BrandSubtitle>
              {t('operator.login.brand_subtitle')}
            </BrandSubtitle>
            <FeatureList>
              <FeatureItem>{t('operator.login.feature_realtime')}</FeatureItem>
              <FeatureItem>{t('operator.login.feature_efficient')}</FeatureItem>
              <FeatureItem>{t('operator.login.feature_accurate')}</FeatureItem>
            </FeatureList>
          </BrandContent>
        </LeftSide>

        <RightSide>
          <LanguageSwitcherWrapper>
            <LanguageSwitcher />
          </LanguageSwitcherWrapper>

          <LoginCard>
            {loginStep === 'credentials' ? (
              <>
                <LoginHeader>
                  <MobileLogo
                    src={getAssetPath('gowarehouse_logo_main')}
                    alt="GoWarehouse"
                  />
                  <VantaBadge>VANTA EDITION</VantaBadge>

                  <RoleBadge>
                    {t('operator.login.role_badge')}
                  </RoleBadge>

                  <LoginTitle>
                    {t('operator.login.title')}
                  </LoginTitle>

                  <LoginSubtitle>
                    {t('operator.login.subtitle')}
                  </LoginSubtitle>
                </LoginHeader>

                <LoginForm
                  name="operator_login"
                  form={form}
                  onFinish={onFinish}
                  size="large"
                  initialValues={{ workspace: initialWorkspace }}
                >
                  {sessionExpiredMessage && (
                    <StyledAlert
                      message={sessionExpiredMessage}
                      type="warning"
                      showIcon
                      closable
                      onClose={() => setSessionExpiredMessage(null)}
                    />
                  )}

                  {authError && (
                    <StyledAlert
                      message={tAuthError(authError, 'login')}
                      type="error"
                      showIcon
                      closable
                      onClose={clearError}
                    />
                  )}

                  {!presetWorkspace && (
                    <Form.Item
                      name="workspace"
                      rules={[
                        { required: true, message: t('operator.login.workspace_required') }
                      ]}
                      normalize={(v: string) => (v ?? '').trim().toLowerCase()}
                    >
                      <StyledInput
                        prefix={<TeamOutlined />}
                        placeholder={t('operator.login.workspace_placeholder')}
                        autoComplete="organization"
                      />
                    </Form.Item>
                  )}

                  <Form.Item
                    name="username"
                    rules={[
                      { required: true, message: t('operator.login.username_required') }
                    ]}
                  >
                    <StyledInput
                      prefix={<UserOutlined />}
                      placeholder={t('operator.login.username_placeholder')}
                      autoComplete="username"
                    />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: t('operator.login.password_required') }
                    ]}
                  >
                    <StyledPasswordInput
                      prefix={<LockOutlined />}
                      placeholder={t('operator.login.password_placeholder')}
                      autoComplete="current-password"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <LoginButton
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                      >
                        {t('operator.login.login_button')}
                      </LoginButton>
                    </Space>
                  </Form.Item>
                </LoginForm>

                <AppSetupTrigger />
                <ClearSessionLink endpoint="/operator/clear_session" />
              </>
            ) : (
              <>
                <LoginHeader>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>
                    <SafetyOutlined style={{ color: '#2D5F9F' }} />
                  </div>

                  <LoginTitle>
                    {t('operator.totp.title')}
                  </LoginTitle>

                  <LoginSubtitle>
                    {useBackupCode
                      ? t('operator.totp.backup_code_subtitle')
                      : t('operator.totp.subtitle')
                    }
                  </LoginSubtitle>
                </LoginHeader>

                <LoginForm
                  name="totp_verify"
                  form={totpForm}
                  onFinish={onTotpFinish}
                  size="large"
                >
                  {authError && (
                    <StyledAlert
                      message={tAuthError(authError, 'totp')}
                      type="error"
                      showIcon
                      closable
                      onClose={clearError}
                    />
                  )}

                  <Form.Item
                    name="code"
                    rules={[
                      { required: true, message: t('operator.totp.code_required') }
                    ]}
                  >
                    <StyledInput
                      prefix={<SafetyOutlined />}
                      placeholder={useBackupCode
                        ? t('operator.totp.backup_code_placeholder')
                        : t('operator.totp.code_placeholder')
                      }
                      autoComplete="one-time-code"
                      maxLength={useBackupCode ? 8 : 6}
                      autoFocus
                    />
                  </Form.Item>

                  <Form.Item>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <LoginButton
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                      >
                        {t('operator.totp.verify_button')}
                      </LoginButton>

                      <div style={{ textAlign: 'center', marginTop: 8 }}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => {
                            setUseBackupCode(!useBackupCode);
                            totpForm.resetFields();
                          }}
                          style={{ color: '#2D5F9F' }}
                        >
                          {useBackupCode
                            ? t('operator.totp.use_authenticator')
                            : t('operator.totp.use_backup_code')
                          }
                        </Button>
                      </div>

                      <div style={{ textAlign: 'center' }}>
                        <Button
                          type="link"
                          size="small"
                          icon={<ArrowLeftOutlined />}
                          onClick={handleBackToCredentials}
                          style={{ color: '#666' }}
                        >
                          {t('operator.totp.back_to_login')}
                        </Button>
                      </div>
                    </Space>
                  </Form.Item>
                </LoginForm>
              </>
            )}
          </LoginCard>
        </RightSide>
      </LoginContainer>
    </>
  );
};

export default OperatorLogin;
